import React, { useCallback, useEffect, useState } from 'react';
import { AsyncTypeahead, Highlighter, InputProps, Menu, MenuItem } from "react-bootstrap-typeahead";
import { Producer, getProducerDisplayName } from "features/settings/producers/producer";
import producerService from "features/settings/producers/producerService";
import './CustomerAccountPicker.scss';
import { ProducerAccount, getProducerAccountDisplayName } from './producerAccount';
import { localizeProducer } from 'features/localization/localizeService';

export interface CustomerAccountPickerProps {
    onCustomerSelected(customer: Producer, account?: ProducerAccount): void;
    selected?: Producer;
    inputProps?: InputProps;
    selectedAccount?: string;
    showClearButton?: boolean;
}

interface CustomerOption {
    customer: Producer;
    account?: ProducerAccount;
    disabled: boolean;
}
const CustomerAccountPicker: React.FC<CustomerAccountPickerProps> = props => {

    const [customers, setCustomers] = useState<Producer[]>([]);
    const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
    const [searchedText, setSearchedText] = useState<string>(null);

    useEffect(() => {
        if (props.selected == null) {
            setCustomers([]);
            setSearchedText(null);
        }
    }, [props.selected])

    const customerOptions = customers.flatMap(m => buildCustomerOption(m));

    if (customerOptions.length === 0 && props.selected != null) {
        customerOptions.push(...buildCustomerOption(props.selected))
    }

    const selectedOption = customerOptions.find(m => m.customer.id === props.selected?.id && m.account?.accountId == props.selectedAccount);

    const doSearch = useCallback(query => {
        searchCustomers(query);
    }, [])

    const customerPageSize = 100;

    function buildCustomerOption(customer: Producer): CustomerOption[] {
        const parentOption: CustomerOption = {
            customer: customer,
            account: null,
            disabled: customer.accounts.length > 0
        };
        return [
            parentOption,
            ...customer.accounts.map(a => ({ ...parentOption, account: a, disabled: false }))
        ];
    }

    async function searchCustomers(search: string) {
        setIsSearchingCustomers(true);
        try {
            if(search && search?.trim() !== '') {
                setSearchedText(search);
                const result = await producerService.listProducersV2(customerPageSize, 0, null, null, search, true, null);
                setCustomers(result.data);
            }
        }
        finally {
            setIsSearchingCustomers(false);
        }
    }

    return <div className="customer-account-picker">
        <AsyncTypeahead
            id="autoComplete-picklist"
            inputProps={{ ...props.inputProps }}
            placeholder={localizeProducer("CustomerAccountPicker_Placeholder_ChooseCustomer")}
            labelKey={(m: CustomerOption) => m.account ? `${getProducerDisplayName(m.customer)} - ${getProducerAccountDisplayName(m.account)}` : `${getProducerDisplayName(m.customer)}`}
            filterBy={() => true}
            renderMenu={(o, menuProps) => {
                return (
                    <Menu {...menuProps}>
                        {customerOptions.map((opt, idx) => (
                            opt.account == null
                                ? <MenuItem key={opt.customer.id}
                                    option={opt}
                                    position={idx}
                                    disabled={opt.disabled}
                                >
                                    <Highlighter search={searchedText}>
                                        {getProducerDisplayName(opt.customer)}
                                    </Highlighter>
                                </MenuItem>
                                :
                                <MenuItem key={`${opt.customer.id}-${opt.account.accountId}`}
                                    className="shifted"
                                    option={opt}
                                    position={idx}
                                >
                                    <Highlighter search={searchedText}>
                                        {`${getProducerAccountDisplayName(opt.account)}`}
                                    </Highlighter>
                                </MenuItem>)
                        )}
                    </Menu>
                )
            }}
            options={customerOptions}
            paginate={false}

            maxResults={customerPageSize}
            minLength={0}
            clearButton={props.showClearButton}
            selected={selectedOption == null ? null : [selectedOption]}
            onChange={selectedVals => {
                const selected = selectedVals == null ? null : selectedVals[0];
                props.onCustomerSelected(selected?.customer, selected?.account);
            }}

            onSearch={doSearch}
            isLoading={isSearchingCustomers}
            delay={150}
            useCache
        />
    </div>
}

export default CustomerAccountPicker;